import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  useTheme,
  Chip,
  Paper,
} from '@mui/material';
import {
  List as ListIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  BarChart as BarChartIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Science as ScienceIcon,
  TrendingUp as TrendingUpIcon,
  Map as MapIcon,
  VerifiedUser as VerifiedUserIcon,
  RateReview as RateReviewIcon,
  AccountTree as AccountTreeIcon,
  ReportProblem as ReportProblemIcon,
  DocumentScanner as DocumentScannerIcon,
  Schema as SchemaIcon,
  Inventory2 as InventoryIcon,
  Spellcheck as SpellcheckIcon,
  School as SchoolIcon,
  Apartment as ApartmentIcon,
  Memory as MemoryIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { ROUTES } from '../config/shared_config';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleNavItems, ADMIN_ROLE_LABELS } from '../config/adminRoles';

const drawerWidth = 240;

const NAV_ICONS = {
  dashboard: HomeIcon,
  exercises: ListIcon,
  users: PersonIcon,
  stats: BarChartIcon,
  'consommation-ia': MemoryIcon,
  'entonnoir-achat': ShoppingCartIcon,
  'prompt-engineering': ScienceIcon,
  marketing: TrendingUpIcon,
  'teacher-pages': SchoolIcon,
  etablissements: ApartmentIcon,
  adventures: MapIcon,
  scenarios: SchemaIcon,
  'products-audit': VerifiedUserIcon,
  grimoire: InventoryIcon,
  impressions: LocalShippingIcon,
  'offres-du-moment': EventNoteIcon,
  'catalog-items': InventoryIcon,
  'parent-feedbacks': RateReviewIcon,
  'exercise-taxonomy': AccountTreeIcon,
  'serving-gaps': ReportProblemIcon,
  'notions-demandees': DocumentScannerIcon,
  'taxonomy-referential': SchemaIcon,
  'error-classification': SpellcheckIcon,
  signalements: ReportProblemIcon,
};

const NAV_LABELS = {
  dashboard: 'Tableau de bord',
  exercises: 'Exercices',
  users: 'Utilisateurs',
  stats: 'Statistiques',
  'consommation-ia': 'Consommation IA',
  'entonnoir-achat': 'Entonnoir d’achat',
  'prompt-engineering': 'Prompt Engineering',
  marketing: 'Marketing',
  'teacher-pages': 'Pages enseignants',
  etablissements: 'Établissements',
  adventures: 'Aventures',
  scenarios: 'Scénarios et variantes',
  'products-audit': 'Audit produits',
  grimoire: 'Grimoire, pass et histoires',
  impressions: 'Grimoires imprimés',
  'offres-du-moment': 'Calendrier des offres',
  'catalog-items': 'Catalogue items',
  'parent-feedbacks': 'Feedbacks parents',
  signalements: 'Signalements',
  'exercise-taxonomy': 'Taxonomie exercices',
  'serving-gaps': 'Trous de service',
  'notions-demandees': 'Notions demandées',
  'taxonomy-referential': 'Référentiel compétences',
  'error-classification': 'Classification fautes',
};

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user, isAdmin, adminRole, checking, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  React.useEffect(() => {
    if (checking) return;
    if (!user || !isAdmin) {
      navigate(ROUTES.LOGIN);
    }
  }, [user, isAdmin, checking, navigate]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(ROUTES.LOGIN, { replace: true });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const visibleNavItems = getVisibleNavItems(adminRole);

  const generateBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    
    return (
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link 
          color="inherit" 
          onClick={() => navigate(visibleNavItems[0]?.path || ROUTES.ADMIN.HOME)}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Admin
        </Link>
        
        {pathnames.slice(1).map((name, index) => {
          const routeTo = `/${pathnames.slice(0, index + 2).join('/')}`;
          const isLast = index === pathnames.slice(1).length - 1;
          
          let label = NAV_LABELS[name] || name;
          if (name === 'exercises') {
            label = 'Exercices';
          } else if (name === 'users') {
            label = 'Utilisateurs';
          } else if (name === 'stats') {
            label = 'Statistiques';
          } else if (name === 'marketing') {
            label = 'Marketing';
          } else if (name === 'adventures') {
            label = 'Aventures';
          } else if (name === 'exercise-taxonomy') {
            label = 'Taxonomie exercices';
          } else if (name === 'create') {
            label = 'Créer';
          }
          
          return isLast ? (
            <Typography key={name} color="textPrimary">
              {label}
            </Typography>
          ) : (
            <Link
              key={name}
              color="inherit"
              onClick={() => navigate(routeTo)}
              sx={{ cursor: 'pointer' }}
            >
              {label}
            </Link>
          );
        })}
      </Breadcrumbs>
    );
  };

  const drawerContent = (
    <>
      <Toolbar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" noWrap component="div">
            Admin DictoQuest
          </Typography>
          {adminRole && (
            <Chip
              size="small"
              label={ADMIN_ROLE_LABELS[adminRole] || adminRole}
              sx={{ mt: 0.5 }}
              color={adminRole === 'admin' ? 'warning' : 'info'}
            />
          )}
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {visibleNavItems.map((item) => {
          const Icon = NAV_ICONS[item.key];
          const selected = item.key === 'exercises'
            ? location.pathname.includes('/admin/exercises')
              && !location.pathname.startsWith('/admin/exercise-taxonomy')
            : item.key === 'exercise-taxonomy'
              ? location.pathname.startsWith('/admin/exercise-taxonomy')
            : item.key === 'adventures'
              ? location.pathname.startsWith('/admin/adventures')
              : location.pathname === item.path;

          return (
            <ListItem
              key={item.key}
              button
              selected={selected}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={NAV_LABELS[item.key]} />
            </ListItem>
          );
        })}
      </List>
      <Divider />
      <List>
        <ListItem 
          button 
          onClick={handleLogout}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Se déconnecter" />
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Administration DictoQuest
          </Typography>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent}
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: '100%',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default 
        }}
      >
        <Toolbar />
      
          <Box sx={{ width: '100%' }}>
            {generateBreadcrumbs()}
            <Paper sx={{ p: 3, mx: 2 }}>
              {children}
            </Paper>
          </Box>
       
      </Box>
    </Box>
  );
}
